"use client"

import { useState } from "react"
import { formatDistanceToNow } from "date-fns"
import {
  IconTarget,
  IconRefresh,
  IconLoader2,
  IconTrophy,
  IconCalendar,
  IconHistory,
} from "@tabler/icons-react"
import { useTranslations } from "next-intl"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { ApolloPanel } from "@/components/ui/apollo-panel"
import { Skeleton } from "@/components/ui/skeleton"
import { toast } from "sonner"

import { type MyProgressResponse } from "@/lib/targets-api"
import { PerformanceHistorySheet } from "./performance-history-sheet"
import { useFormatCurrency } from "@/hooks/use-currency"

interface TargetProgressWidgetProps {
  data: MyProgressResponse | null
  isLoading: boolean
  onRefresh: () => Promise<void>
}

export function TargetProgressWidget({ data, isLoading, onRefresh }: TargetProgressWidgetProps) {
  const t = useTranslations("dashboard")
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [historyOpen, setHistoryOpen] = useState(false)

  const handleRefresh = async () => {
    setIsRefreshing(true)
    try {
      await onRefresh()
      toast.success(t("target.toast.refreshed"))
    } catch {
      toast.error(t("target.toast.refreshFailed"))
    } finally {
      setIsRefreshing(false)
    }
  }

  const { formatCurrencyNoDecimals } = useFormatCurrency()

  const formatValue = (value: number, metric?: string) => {
    if (metric === "quotes_accepted_revenue") {
      return formatCurrencyNoDecimals(value)
    }
    return value.toString()
  }

  if (isLoading) {
    return (
      <ApolloPanel eyebrow={t("target.header")}>
        <div className="space-y-4">
          <div className="space-y-2">
            <div className="flex justify-between">
              <Skeleton className="h-4 w-16" />
              <Skeleton className="h-4 w-12" />
            </div>
            <Skeleton className="h-2 w-full rounded-full" />
          </div>
          <div className="flex justify-between items-end">
            <div className="space-y-1">
              <Skeleton className="h-8 w-20" />
              <Skeleton className="h-3 w-16" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 pt-3 border-t">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
          </div>
          <div className="grid grid-cols-3 gap-2 pt-2 border-t">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="flex flex-col items-center gap-1">
                <Skeleton className="h-5 w-8" />
                <Skeleton className="h-3 w-12" />
              </div>
            ))}
          </div>
        </div>
      </ApolloPanel>
    )
  }

  if (!data?.has_active_period || !data.progress) {
    return (
      <ApolloPanel eyebrow={t("target.header")}>
        <div className="flex flex-col items-center justify-center py-6 text-center">
          <div className="rounded-full bg-muted p-3 mb-3">
            <IconTarget className="h-6 w-6 text-muted-foreground" />
          </div>
          <p className="text-sm font-medium text-muted-foreground">
            {t("target.empty.title")}
          </p>
          <p className="text-xs text-muted-foreground/70 mt-1">
            {t("target.empty.body")}
          </p>
        </div>
      </ApolloPanel>
    )
  }

  const { progress, period } = data
  const percentage = Math.min(progress.percentage, 100)
  const progressColor =
    percentage >= 100
      ? "bg-green-500"
      : percentage >= 80
      ? "bg-yellow-500"
      : "bg-blue-500"

  return (
    <ApolloPanel
      eyebrow={t("target.header")}
      actions={
        <>
          {period && (
            <Badge variant="outline" className="text-[10px]">
              {period.metric_label}
            </Badge>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={() => setHistoryOpen(true)}
            title={t("target.viewHistory")}
          >
            <IconHistory className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={handleRefresh}
            disabled={isRefreshing}
            title={t("target.refresh")}
          >
            {isRefreshing ? (
              <IconLoader2 className="h-4 w-4 animate-spin" />
            ) : (
              <IconRefresh className="h-4 w-4" />
            )}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {/* Progress Bar */}
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">{t("target.progress")}</span>
            <span className="font-medium">{progress.percentage.toFixed(1)}%</span>
          </div>
          <div className="h-2 bg-muted rounded-full overflow-hidden">
            <div
              className={`h-full ${progressColor} transition-all duration-500`}
              style={{ width: `${percentage}%` }}
            />
          </div>
        </div>

        {/* Values */}
        <div className="flex justify-between items-end">
          <div>
            <p className="font-serif text-[26px] leading-none tracking-[-0.01em] tabular-nums">
              {formatValue(progress.current_value, period?.metric)}
            </p>
            <p className="text-xs text-muted-foreground mt-1.5">
              {t("target.of", { target: formatValue(progress.target_value, period?.metric) })}
            </p>
          </div>
          {progress.tier_achieved && (
            <div className="text-right">
              <Badge className="bg-green-600">
                <IconTrophy className="h-3 w-3 mr-1" />
                {formatValue(progress.bonus_earned || 0, "quotes_accepted_revenue")}
              </Badge>
            </div>
          )}
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 gap-4 pt-3 border-t text-sm">
          <div>
            <p className="text-muted-foreground text-xs">{t("target.daysRemaining")}</p>
            <p className="font-medium flex items-center gap-1">
              <IconCalendar className="h-3.5 w-3.5" />
              {progress.days_remaining}
            </p>
          </div>
          <div className="text-right">
            <p className="text-muted-foreground text-xs">{t("target.lastUpdated")}</p>
            <p className="font-medium text-xs">
              {formatDistanceToNow(new Date(progress.last_calculated_at), { addSuffix: true })}
            </p>
          </div>
        </div>

        {/* Activity Stats */}
        <div className="grid grid-cols-3 gap-2 pt-2 border-t">
          <div className="text-center">
            <p className="text-lg font-semibold tabular-nums">{progress.status_updates_count}</p>
            <p className="text-[10px] text-muted-foreground">{t("target.statUpdates")}</p>
          </div>
          <div className="text-center">
            <p className="text-lg font-semibold tabular-nums">{progress.comments_count}</p>
            <p className="text-[10px] text-muted-foreground">{t("target.statComments")}</p>
          </div>
          <div className="text-center">
            <p className="text-lg font-semibold tabular-nums">{progress.quotes_sent_count}</p>
            <p className="text-[10px] text-muted-foreground">{t("target.statQuotesSent")}</p>
          </div>
        </div>
      </div>

      <PerformanceHistorySheet
        open={historyOpen}
        onOpenChange={setHistoryOpen}
      />
    </ApolloPanel>
  )
}
